import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimesCircle, 
  faShoppingCart, 
  faHome, 
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const CheckoutCancelPage: React.FC = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const sessionIdParam = searchParams.get('session_id');
      setSessionId(sessionIdParam);
      
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
      
    } catch (err) {
      console.error('CheckoutCancelPage: Error processing URL params:', err);
      setError('Error processing checkout cancellation');
      setIsLoading(false);
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="pt-20 min-h-screen bg-gradient-to-br from-[#2b603e] via-[#1a4a2a] to-[#0f2d1a] flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-sm p-12 rounded-2xl shadow-2xl max-w-md">
          <FontAwesomeIcon 
            icon={faSpinner} 
            className="text-[#C2C764] text-6xl mb-6 mx-auto animate-spin" 
          />
          <h1 className="text-3xl font-bold text-white mb-4">
            Processing...
          </h1>
          <p className="text-white/80">
            Please wait while we process your checkout cancellation.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-20 min-h-screen bg-gradient-to-br from-red-600 via-red-700 to-red-800 flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-sm p-12 rounded-2xl shadow-2xl max-w-md">
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            className="text-red-300 text-6xl mb-6 mx-auto" 
          />
          <h1 className="text-3xl font-bold text-white mb-4">
            Error
          </h1>
          <p className="text-white/80 mb-6">
            {error}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/" 
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full transition-colors font-semibold"
            >
              Go Home
            </Link>
            <Link 
              to="/products" 
              className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full transition-colors font-semibold"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-[#2b603e] via-[#1a4a2a] to-[#0f2d1a] flex items-center justify-center">
      <div className="container mx-auto px-4">
        <div className="text-center bg-white/10 backdrop-blur-sm p-12 rounded-2xl shadow-2xl max-w-2xl mx-auto">
          <div className="mb-8">
            <FontAwesomeIcon 
              icon={faTimesCircle} 
              className="text-red-400 text-8xl mb-6 mx-auto" 
            />
            <h1 className="text-4xl font-bold text-white mb-4">
              Checkout Canceled
            </h1>
            <p className="text-xl text-white/80 mb-6 leading-relaxed">
              Your checkout session has been canceled. No charges have been made to your account.
            </p>
            {sessionId && (
              <p className="text-sm text-white/60 font-mono bg-white/5 px-4 py-2 rounded-lg inline-block">
                Session ID: {sessionId}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link 
              to="/checkout" 
              className="group inline-flex items-center justify-center bg-[#C2C764] hover:bg-[#a8b04a] text-[#2b603e] font-bold py-4 px-8 rounded-full transition-all duration-300 text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <FontAwesomeIcon icon={faShoppingCart} className="mr-3" />
              Return to Cart
            </Link>
            <Link 
              to="/products" 
              className="group inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 text-lg border-2 border-white/30 hover:border-white/50 backdrop-blur-sm"
            >
              <FontAwesomeIcon icon={faShoppingCart} className="mr-3" />
              Continue Shopping
            </Link>
          </div>

          {/* Additional Help */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-3">Need Help?</h3>
            <p className="text-white/70 text-sm mb-4">
              If you experienced any issues during checkout or need assistance, please don't hesitate to contact our support team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link 
                to="/contact" 
                className="text-[#C2C764] hover:text-[#a8b04a] text-sm font-medium transition-colors"
              >
                Contact Support
              </Link>
              <span className="text-white/30 hidden sm:inline">•</span>
              <Link 
                to="/" 
                className="text-white/70 hover:text-white text-sm font-medium transition-colors flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faHome} className="mr-2" />
                Go Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutCancelPage; 